// ─── seo.js ───────────────────────────────────────────────────────────────────
// Per-route <head> management + structured-data builders.
//
// applyHead() imperatively upserts title/meta/canonical/JSON-LD by selector, so
// it OVERRIDES index.html's static (homepage) tags in place instead of leaving
// duplicates. The post-build headless prerender (scripts/prerender.mjs) runs
// these effects and dumps the resulting <head>, so crawlers get correct,
// per-URL meta + schema. The sitewide LocalBusiness @graph lives in index.html
// and is captured into every prerendered page; pages add their own @graph here.
// ──────────────────────────────────────────────────────────────────────────────

export const SITE = {
  origin: 'https://brocodebarbershop.ca',
  name: 'Bro Code Barbershop',
  logo: 'https://brocode.b-cdn.net/logo.png',
  phone: '+1-236-899-0603',
  booking: 'https://getsquire.com/booking/brands/bro-code-barbershop',
  instagram: 'https://www.instagram.com/bro_code_barbershop/',
};

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * Set the document head for a route. Returns a cleanup that removes only the
 * page-scoped JSON-LD (title/meta persist until the next route sets them).
 */
export function applyHead({ title, description, canonical, image, ogType = 'website', jsonLd }) {
  if (title) document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', 'index, follow');

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:type', ogType);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);

  upsertLink('canonical', canonical);

  if (jsonLd) {
    let script = document.head.querySelector('script[data-seo-ld]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-ld', '1');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
  }

  return () => {
    const script = document.head.querySelector('script[data-seo-ld]');
    if (script) script.remove();
  };
}

/** Service + FAQPage + BreadcrumbList @graph for a service/location page. */
export function buildServiceSchema(page, origin = SITE.origin) {
  const pageUrl = `${origin}/${page.slug}`;

  const areaServed = [...new Set([...(page.locations || []), 'Fraser Valley'])]
    .map((name) => ({ '@type': 'City', name }));

  const provider = {
    '@type': 'BarberShop',
    name: SITE.name,
    url: origin,
    telephone: SITE.phone,
    image: SITE.logo,
  };

  const service = {
    '@type': 'Service',
    name: page.h1,
    description: page.metaDescription,
    image: page.heroImage,
    serviceType: page.h1,
    areaServed,
    provider,
    url: pageUrl,
  };

  const faqPage = {
    '@type': 'FAQPage',
    mainEntity: (page.faqs || []).map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: origin },
      { '@type': 'ListItem', position: 2, name: page.h1, item: pageUrl },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [service, faqPage, breadcrumbList],
  };
}

/** Per-page meta for the non-service routes. */
export const PAGE_META = {
  '/': {
    title: 'Bro Code Barbershop | Abbotsford & Surrey Barbers',
    description:
      'Award-winning barbershop in Abbotsford & Surrey with 400+ five-star reviews and expert barbers. Serving the Fraser Valley — book your cut online today.',
    canonical: `${SITE.origin}/`,
    image: 'https://brocode.b-cdn.net/hf_20260315_193425_f9b645e9-d929-4d2d-b0cf-15a2e106c8c1.jpeg',
  },
  '/gallery': {
    title: 'Haircut & Fade Gallery | Bro Code Barbershop Abbotsford',
    description:
      "Browse real fades, tapers & beard work from Bro Code Barbershop's Abbotsford & Surrey shops. 400+ five-star reviews — see the cuts, then book yours.",
    canonical: `${SITE.origin}/gallery`,
    image: 'https://brocode.b-cdn.net/brocode/98AED732-B703-43F8-89D7-4F6F52DC6D02.jpg',
  },
  '/team': {
    title: 'Meet Our Barbers | Bro Code Barbershop Surrey & Abbotsford',
    description:
      "Meet the award-winning barbers & tattoo artists behind Bro Code Barbershop's 400+ five-star reviews in Abbotsford, Surrey & the Fraser Valley. Book now.",
    canonical: `${SITE.origin}/team`,
    image: 'https://brocode.b-cdn.net/barbers/DSC03734.JPG',
  },
};
